/**
 * Marquee — auto-fill the track with set clones until total track width
 * is at least 2× the container width, so the infinite scroll never reveals
 * empty space. Animation translates by exactly 1-set-width per cycle so the
 * seam stays invisible regardless of set count.
 *
 * Hooks are data attributes, not block classes, so any block can opt in:
 * [data-marquee] root, [data-marquee-track], [data-marquee-set].
 *
 * @package Felix
 */

const CLONE_ATTR = 'data-marquee-clone'

function fillMarquee(marquee) {
	const track = marquee.querySelector('[data-marquee-track]')
	if (!track) return
	const original = track.querySelector('[data-marquee-set]')
	if (!original) return

	// Drop any previous JS clones so we recalculate from a clean baseline.
	track.querySelectorAll(`[${CLONE_ATTR}]`).forEach((c) => c.remove())

	const setWidth = original.getBoundingClientRect().width
	if (!setWidth) return
	const containerWidth = marquee.getBoundingClientRect().width
	// Total track width should be at least 2× container so the visible area
	// always has content while the animation runs.
	const currentSets = track.querySelectorAll('[data-marquee-set]').length
	const minSets = Math.max(2, Math.ceil((2 * containerWidth) / setWidth))
	const clonesNeeded = Math.max(0, minSets - currentSets)

	for (let i = 0; i < clonesNeeded; i++) {
		const clone = original.cloneNode(true)
		clone.setAttribute(CLONE_ATTR, '')
		clone.setAttribute('aria-hidden', 'true')
		track.appendChild(clone)
	}

	const totalSets = currentSets + clonesNeeded
	// Translate by exactly one set's width per animation cycle so the seam
	// lands on identical content and the loop is invisible.
	track.style.setProperty('--marquee-distance', `-${100 / totalSets}%`)
}

function setupMarquee(marquee) {
	fillMarquee(marquee)

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(() => fillMarquee(marquee))
		observer.observe(marquee)
	} else {
		// Fallback for older browsers
		window.addEventListener('resize', () => fillMarquee(marquee))
	}
}

export function initMarquees() {
	document.querySelectorAll('[data-marquee]').forEach(setupMarquee)
}
